/**
 * The declared-column-fact framework — shared types for the meaning-changing,
 * per-column interpretations that are INFERRED from a column's own values, shown
 * as a visible, correctable control on the mapping screen, persisted per column,
 * and applied as the authoritative transform when the value is used. The R3
 * contract made structural: the fact is never silently guessed from ambiguous
 * data — each fact's POLICY decides whether ambiguity blocks or applies a
 * visible, correctable default.
 *
 * A declared column fact is:
 *  1. INFERRED — a pure vote over the column's own cells produces a `suggested`
 *     value plus an `ambiguous` flag ({@link AmbiguityResult}).
 *  2. SURFACED — rendered as a chip/select on the mapping row, seeded by
 *     `suggested` and labeled "matches your data".
 *  3. CORRECTABLE — the customer's chosen value wins; `suggested` only defaults.
 *  4. AMBIGUITY POLICY (per fact) — `ambiguous: true` means the data admits
 *     multiple readings. What happens next is the FACT's policy, applied by the
 *     resolver/UI layer, never inside a detector or parser: reporting basis
 *     BLOCKS Continue/finalize until the customer picks; date format PREFILLS
 *     the US Month/Day default with `suggested` provenance and NEVER blocks
 *     (founder-ruled 2026-07-18: US-only customer base), with any cell that
 *     does not fit the effective format reading as no value (wrong_format).
 *  5. PERSISTED per column — on the wire (`ColumnMapping`), on `mapping_fields`,
 *     and durably on `portfolio_fields` (with provenance), mirroring the
 *     reporting-basis rail.
 *  6. AUTHORITATIVE — the resolved fact drives the transform where the value is
 *     used (percent → ×100-or-identity at projection; date → which `YYYYMMDD`
 *     ordinal the raw text produces at ingest).
 *
 * Existing instances (the pattern this module names, not re-implements):
 *  - #0 REPORTING BASIS (`./reporting-basis`) — per-column running-total vs
 *    per-period declaration; resolver `resolveReportingBasisState`, stored as
 *    `portfolio_fields.reportingBasis` + provenance, blocking chip on the
 *    mapping row (`enforceReportingBasesDeclared` server gate).
 *  - #1 PERCENT VARIANT (`./type-inference` `inferNumericSubtype` +
 *    `defaultPercentVariant`) — fraction vs percent-points; stored as
 *    `portfolio_fields.valueSubtype`, surfaced by `PercentVariantSelect`.
 *    Never must-ask: a magnitude-based default always exists.
 *  - #2 DATE FORMAT (`./date-parse` `detectDateColumnFormat` +
 *    `parseDateWithFormat`) — a slash/dash column whose days never exceed 12
 *    cannot be READ from its own data; per the 2026-07-18 founder ruling the
 *    policy layer prefills the US Month/Day default (provenance `suggested`,
 *    correctable, never blocking) rather than asking.
 *
 * This module is dependency-free (types + tiny constants only) so the UI, the
 * wire contracts, the detectors, and the SQL mirrors can all reference one
 * definition without pulling in any parser.
 */

import type { NumericSubtype } from './types';

/** The known declared-column-fact kinds (see the module doc for each instance). */
export const DECLARED_FACT_KINDS = [
  'reporting_basis',
  'percent_variant',
  'date_format',
] as const;
export type DeclaredFactKind = (typeof DECLARED_FACT_KINDS)[number];

/**
 * The percent-variant fact's value domain — re-exported here so declared-fact
 * consumers can reference instance #1 without importing the subtype model.
 */
export type PercentVariant = Extract<NumericSubtype, 'percentage' | 'percent_points'>;

/**
 * The date-format fact's value domain (instance #2):
 *  - `iso` — year-first calendar dates and period codes (`2024-01-15`,
 *    `2024/1/15`, `20240115`, `2024-01`, `202401`). Always unambiguous.
 *  - `mdy` — two-number Month/Day/Year order (`04/07/2025` = April 7,
 *    `01-15-2024` = January 15).
 *  - `dmy` — two-number Day/Month/Year order (`04/07/2025` = July 4).
 *  - `month_name` — written month names (`Jan-2024`, `January 2024`,
 *    `15-Jan-2024`, `Jan 15, 2024`). Always unambiguous.
 */
export const DECLARED_DATE_FORMATS = ['iso', 'mdy', 'dmy', 'month_name'] as const;
export type DeclaredDateFormat = (typeof DECLARED_DATE_FORMATS)[number];

/**
 * The shared output shape of a declared-fact detector: a pure vote over the
 * column's own cells.
 *
 * `ambiguous: true` means the data admits more than one reading and the
 * detector suggests nothing — the fact's POLICY layer then decides: block
 * finalize (reporting basis) or prefill a visible, correctable default (date
 * format → US Month/Day, founder-ruled 2026-07-18). When `ambiguous` is false,
 * `suggested` (when non-null) is a data-proven default the customer can still
 * override.
 */
export interface AmbiguityResult<T> {
  /** The inferred value, or null when the data cannot pick one. */
  readonly suggested: T | null;
  /** True when the data admits multiple readings — the must-ask case. */
  readonly ambiguous: boolean;
  /** Non-blank cells that fit NO recognized shape of this fact's domain. */
  readonly unparseableCount: number;
  /** The values of T consistent with the observed data (the choices to offer). */
  readonly candidates: readonly T[];
}

/**
 * The date-format detector's result ({@link AmbiguityResult} plus the
 * conflict marker): `conflict` is true when the column carries POSITIVE evidence
 * for BOTH orientations (some cells prove day-first, others prove month-first) —
 * the data is internally inconsistent, not merely silent. A conflicted column is
 * also `ambiguous` (the customer must still choose), but the UI can additionally
 * warn that some cells will be unparseable under EITHER choice.
 */
export interface DateFormatDetection extends AmbiguityResult<DeclaredDateFormat> {
  readonly conflict: boolean;
}

/**
 * A declared-fact detector: a pure function over a column's raw cells. Both
 * shipped detectors (`detectDateColumnFormat`; percent's share/magnitude vote)
 * conform to this shape so future instances slot into the same UI machinery.
 */
export type DeclaredFactDetector<T> = (
  cells: readonly string[],
) => AmbiguityResult<T>;
